/**
 * Pedagogía piano — registro por manos (Do central), pasos nivel 1 y melodías.
 * Do4 (MIDI 60) hacia arriba = mano izquierda; Si3 (59) hacia abajo = mano derecha.
 */

import {
  buildScalePattern,
  noteToSemitones,
  SCALE_INTERVALS,
  type NoteName,
} from "./guitarEngine";
import { HAND_SPLIT_MIDI, MIDDLE_C } from "./keyboardEngine";
import type { PianoFingeredKey } from "./pianoFingeringEngine";
import type { PracticeNoteEvent } from "./practiceNoteModel";

export { HAND_SPLIT_MIDI, MIDDLE_C };

export type PianoFocusStep = 1 | 2 | 3 | 4 | 5;

/** En pasos 2 y 3: primero ascendente, luego descendente. */
export type PianoIntervalDirection = "asc" | "desc";

export const PIANO_LEVEL1_STEPS: ReadonlyArray<{
  level: PianoFocusStep;
  short: string;
  label: string;
  color: string;
}> = [
  { level: 1, short: "I", label: "Tónica", color: "#E63946" },
  { level: 2, short: "2", label: "2das ↑↓", color: "#5BA8A8" },
  { level: 3, short: "5", label: "5tas ↑↓", color: "#D4A373" },
  { level: 4, short: "1–5", label: "Camino", color: "#8FB069" },
  { level: 5, short: "♪", label: "Pollitos", color: "#9B5DE5" },
];

/** Melodía «Los pollitos dicen» en Do mayor (una octava, registro mano izquierda). */
export const LOS_POLLITOS_C_MAJOR_MIDI: readonly number[] = [
  60, 62, 64, 65, 67, 67, 69, 69, 69, 69, 67, 67, 65, 65, 65, 65, 64, 64, 64, 64, 62, 62,
  62, 62, 60, 60,
];

export function pianoHandForMidi(midi: number): "L" | "R" {
  return midi >= HAND_SPLIT_MIDI ? "L" : "R";
}

export function rootMidiAtOctave(root: NoteName, octave = 4): number {
  return noteToSemitones(root) + (octave + 1) * 12;
}

function transposeMidis(midis: readonly number[], root: NoteName): number[] {
  const offset = noteToSemitones(root) - noteToSemitones("C");
  return midis.map((m) => m + offset);
}

function semitoneToNote(midi: number): NoteName {
  const names = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const;
  return names[((midi % 12) + 12) % 12] as NoteName;
}

function degreeLabel(scaleType: string, semitoneFromRoot: number): string {
  const intervals = SCALE_INTERVALS[scaleType] ?? SCALE_INTERVALS.major;
  const idx = intervals.indexOf(((semitoneFromRoot % 12) + 12) % 12);
  const pattern = buildScalePattern("C", scaleType);
  if (idx >= 0) return pattern.degrees[idx] ?? "?";
  if (semitoneFromRoot === 0) return "R";
  return "?";
}

/** MIDI visibles + dedaje para el paso actual (nivel 1 piano). */
export function buildPianoLevel1Fingering(
  root: NoteName,
  scaleType: string,
  step: PianoFocusStep
): PianoFingeredKey[] {
  const intervals = SCALE_INTERVALS[scaleType] ?? SCALE_INTERVALS.major;
  const rootMidi = rootMidiAtOctave(root, 4);
  const second = intervals[1] ?? 2;
  const fifth = intervals[4] ?? 7;

  const mk = (
    midi: number,
    finger: 1 | 2 | 3 | 4 | 5,
    degree: string
  ): PianoFingeredKey => ({
    midi,
    finger,
    hand: pianoHandForMidi(midi),
    degree,
    step: semitoneToNote(midi),
  });

  switch (step) {
    case 1: {
      const r5 = rootMidi + 12;
      return [mk(rootMidi, 1, "R"), mk(r5, 1, "R")].filter(
        (k) => k.midi >= HAND_SPLIT_MIDI - 12
      );
    }
    case 2:
      return [
        mk(rootMidi, 1, "R"),
        mk(rootMidi + second, 2, "2"),
        mk(rootMidi - second, 2, "2"),
      ].filter((k) => k.midi >= HAND_SPLIT_MIDI - 12);
    case 3:
      return [
        mk(rootMidi, 1, "R"),
        mk(rootMidi + fifth, 5, "5"),
        mk(rootMidi - fifth, 5, "5"),
      ].filter((k) => k.midi >= HAND_SPLIT_MIDI - 12);
    case 4: {
      const path = [0, second, intervals[2] ?? 4, intervals[3] ?? 5, fifth].map(
        (st) => rootMidi + st
      );
      const lhFingers: (1 | 2 | 3 | 4 | 5)[] = [5, 4, 3, 2, 1];
      return path.map((midi, i) =>
        mk(midi, lhFingers[i] ?? 1, i === 0 ? "R" : degreeLabel(scaleType, midi - rootMidi))
      );
    }
    case 5: {
      const midis = transposeMidis(LOS_POLLITOS_C_MAJOR_MIDI, root);
      return midis.map((midi, i) => {
        const finger = ((i % 5) + 1) as 1 | 2 | 3 | 4 | 5;
        return mk(midi, finger, i === 0 || i === midis.length - 1 ? "R" : "2");
      });
    }
    default:
      return [mk(rootMidi, 1, "R")];
  }
}

/** MIDI de la 2ª o 5ª en una dirección (escala mayor: 2ª asc = 2M, 2ª desc = 2m). */
export function getPianoIntervalTargetMidi(
  root: NoteName,
  scaleType: string,
  step: 2 | 3,
  direction: PianoIntervalDirection
): number {
  const r = rootMidiAtOctave(root, 4);
  if (step === 2) {
    const majorSecond = (SCALE_INTERVALS[scaleType] ?? SCALE_INTERVALS.major)[1] ?? 2;
    return direction === "asc" ? r + majorSecond : r - 1;
  }
  const fifth = (SCALE_INTERVALS[scaleType] ?? SCALE_INTERVALS.major)[4] ?? 7;
  return direction === "asc" ? r + fifth : r - fifth;
}

export function pianoIntervalLabel(
  step: 2 | 3,
  direction: PianoIntervalDirection,
  root: NoteName
): string {
  const note = semitoneToNote(getPianoIntervalTargetMidi(root, "major", step, direction));
  if (step === 2) {
    return direction === "asc"
      ? `2ª ascendente (2M) — ${note}`
      : `2ª descendente (2m) — ${note}`;
  }
  return direction === "asc"
    ? `5ª ascendente (5J) — ${note}`
    : `5ª descendente (5J) — ${note}`;
}

/** Secuencia de reproducción / práctica (solo notas del paso). */
export function buildPianoLevel1PlaybackMidis(
  root: NoteName,
  scaleType: string,
  step: PianoFocusStep,
  intervalDir?: PianoIntervalDirection
): number[] {
  switch (step) {
    case 1:
      return [rootMidiAtOctave(root, 4), rootMidiAtOctave(root, 5)];
    case 2: {
      if (intervalDir) {
        return [getPianoIntervalTargetMidi(root, scaleType, 2, intervalDir)];
      }
      const r = rootMidiAtOctave(root, 4);
      const second = (SCALE_INTERVALS[scaleType] ?? SCALE_INTERVALS.major)[1] ?? 2;
      return [r + second, r - 1];
    }
    case 3: {
      if (intervalDir) {
        return [getPianoIntervalTargetMidi(root, scaleType, 3, intervalDir)];
      }
      const r = rootMidiAtOctave(root, 4);
      const fifth = (SCALE_INTERVALS[scaleType] ?? SCALE_INTERVALS.major)[4] ?? 7;
      return [r + fifth, r - fifth];
    }
    case 4: {
      const intervals = SCALE_INTERVALS[scaleType] ?? SCALE_INTERVALS.major;
      const r = rootMidiAtOctave(root, 4);
      return [0, intervals[1] ?? 2, intervals[2] ?? 4, intervals[3] ?? 5, intervals[4] ?? 7].map(
        (st) => r + st
      );
    }
    case 5:
      return transposeMidis(LOS_POLLITOS_C_MAJOR_MIDI, root);
    default:
      return [rootMidiAtOctave(root, 4)];
  }
}

export function buildPianoHighlights(
  root: NoteName,
  scaleType: string,
  pedagogicalLevel: number,
  focusStep: number,
  playingIndex?: number,
  isPlaying?: boolean
): PianoFingeredKey[] {
  if (pedagogicalLevel !== 1 || focusStep < 1 || focusStep > 5) {
    const rootMidi = rootMidiAtOctave(root, 4);
    const pattern = buildScalePattern(root, scaleType);
    const intervals = SCALE_INTERVALS[scaleType] ?? SCALE_INTERVALS.major;
    return pattern.notes.map((note, i) => {
      const midi = rootMidi + (intervals[i] ?? 0);
      return {
        midi,
        finger: clampFinger(i + 1),
        hand: pianoHandForMidi(midi),
        degree: pattern.degrees[i] ?? "?",
        step: note,
      };
    });
  }

  const keys = buildPianoLevel1Fingering(root, scaleType, focusStep as PianoFocusStep);
  if (!isPlaying || playingIndex == null) return keys;

  return keys.map((k, i) => ({
    ...k,
    degree: i === playingIndex ? `${k.degree}*` : k.degree,
  }));
}

function clampFinger(n: number): 1 | 2 | 3 | 4 | 5 {
  const f = Math.min(5, Math.max(1, n));
  return f as 1 | 2 | 3 | 4 | 5;
}

/** Notas del paso actual para el pentagrama (solo lo que se pide en Aprender). */
export function buildPianoStepStaffEvents(
  root: NoteName,
  scaleType: string,
  step: PianoFocusStep,
  intervalDir?: PianoIntervalDirection
): PracticeNoteEvent[] {
  const rootMidi = rootMidiAtOctave(root, 4);
  const mk = (
    midi: number,
    degree: string,
    stepNote: NoteName
  ): PracticeNoteEvent => ({
    midi,
    start: 0,
    duration: 1,
    hand: pianoHandForMidi(midi),
    degree,
    step: stepNote,
  });

  if (step === 2 && intervalDir) {
    const target = getPianoIntervalTargetMidi(root, scaleType, 2, intervalDir);
    return [
      mk(rootMidi, "R", root),
      mk(target, intervalDir === "asc" ? "2↑" : "2↓", semitoneToNote(target)),
    ];
  }
  if (step === 3 && intervalDir) {
    const target = getPianoIntervalTargetMidi(root, scaleType, 3, intervalDir);
    return [
      mk(rootMidi, "R", root),
      mk(target, intervalDir === "asc" ? "5↑" : "5↓", semitoneToNote(target)),
    ];
  }

  return buildPianoLevel1Fingering(root, scaleType, step).map((k, i) => ({
    midi: k.midi,
    start: i,
    duration: 1,
    hand: k.hand,
    finger: k.finger,
    degree: k.degree.replace("*", ""),
    step: k.step,
  }));
}

export function buildPianoLearnKeyboardHighlights(
  root: NoteName,
  scaleType: string,
  step: PianoFocusStep,
  intervalDir?: PianoIntervalDirection
): PianoFingeredKey[] {
  if ((step === 2 || step === 3) && intervalDir) {
    const rootMidi = rootMidiAtOctave(root, 4);
    const target = getPianoIntervalTargetMidi(root, scaleType, step, intervalDir);
    const mk = (midi: number, degree: string, finger: 1 | 2 | 3 | 4 | 5): PianoFingeredKey => ({
      midi,
      finger,
      hand: pianoHandForMidi(midi),
      degree,
      step: semitoneToNote(midi),
    });
    const deg =
      step === 2
        ? intervalDir === "asc"
          ? "2↑"
          : "2↓"
        : intervalDir === "asc"
        ? "5↑"
        : "5↓";
    return [mk(rootMidi, "R", 1), mk(target, deg, 2)];
  }
  return buildPianoLevel1Fingering(root, scaleType, step);
}

export function pianoStepDescription(
  step: PianoFocusStep,
  intervalDir?: PianoIntervalDirection
): string {
  switch (step) {
    case 1:
      return "Encuentra la tónica (Do central hacia arriba, mano izquierda)";
    case 2:
      return intervalDir === "asc"
        ? "Busca la 2ª ascendente (2M) — un paso adelante en la escala"
        : intervalDir === "desc"
        ? "Busca la 2ª descendente (2m) — un semitono atrás desde la tónica"
        : "2ª ascendente (2M) y descendente (2m) desde la tónica";
    case 3:
      return intervalDir === "asc"
        ? "Busca la 5ª ascendente (5J) — cinco grados arriba"
        : intervalDir === "desc"
        ? "Busca la 5ª descendente (5J) — cinco grados abajo"
        : "5ª ascendente y descendente desde la tónica";
    case 4:
      return "Camino tónica → quinta con dedos 5-4-3-2-1";
    case 5:
      return "Los pollitos dicen — Do re mi fa sol…";
    default:
      return "";
  }
}
